import {
  BadgePercent,
  Bell,
  BookOpen,
  Bot,
  ChartPie,
  FlaskConical,
  Gauge,
  HandCoins,
  Inbox,
  Layers,
  type LucideIcon,
  Mail,
  MailPlus,
  Megaphone,
  MessageSquareHeart,
  MousePointerClick,
  Repeat,
  ScrollText,
  Send,
  ShieldCheck,
  Sunrise,
  TextQuote,
  TrendingUp,
  Unplug,
  UserRound,
  Users,
} from 'lucide-react'
import type { StaffRole } from '@/lib/api/types'

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  /** Who sees it; everyone when absent. */
  roles?: StaffRole[]
  /** Opens outside the control panel (Horizon). */
  external?: boolean
  /** Shown as "New" in the menu for a while after it arrives. */
  isNew?: boolean
}

/** The control panel's menu, grouped by the job each page does. */
export const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: 'Today',
    items: [
      { to: '/', label: 'Briefing', icon: Sunrise },
      { to: '/tickets', label: 'Support inbox', icon: Inbox },
      { to: '/notifications', label: 'Notifications', icon: Bell },
    ],
  },
  {
    group: 'Customers',
    items: [
      { to: '/customers', label: 'Customers', icon: Users },
      { to: '/audience', label: 'Audience', icon: ChartPie, isNew: true },
      {
        to: '/website',
        label: 'Website visitors',
        icon: MousePointerClick,
        roles: ['owner', 'admin'],
        isNew: true,
      },
      { to: '/ai-connector', label: 'AI connector', icon: Bot, isNew: true },
      { to: '/referrals', label: 'Referrals', icon: HandCoins },
      { to: '/feedback', label: 'Feedback', icon: MessageSquareHeart, isNew: true },
      { to: '/research-program', label: 'Research program', icon: FlaskConical, isNew: true },
    ],
  },
  {
    group: 'Revenue',
    items: [
      { to: '/sales', label: 'Revenue', icon: TrendingUp },
      { to: '/subscriptions', label: 'Subscriptions', icon: Repeat },
      { to: '/discounts', label: 'Discounts', icon: BadgePercent },
    ],
  },
  {
    group: 'Engage',
    items: [
      { to: '/emails', label: 'Lifecycle emails', icon: Mail },
      { to: '/announcements', label: 'Announcements', icon: Send, isNew: true },
      { to: '/marketing', label: 'Marketing', icon: MailPlus, isNew: true },
      { to: '/help', label: 'Help center', icon: BookOpen },
      { to: '/settings', label: 'Announcements and settings', icon: Megaphone },
    ],
  },
  {
    group: 'Run',
    items: [
      { to: '/team', label: 'Team', icon: ShieldCheck },
      { to: '/audit-log', label: 'Audit log', icon: ScrollText },
      { to: '/limits', label: 'Service limits', icon: Gauge, roles: ['owner'] },
      {
        to: '/connections',
        label: 'Connection errors',
        icon: Unplug,
        roles: ['owner', 'admin'],
        isNew: true,
      },
      { to: '/horizon', label: 'Queues', icon: Layers, roles: ['owner', 'admin'], external: true },
    ],
  },
]

/** Pages that aren't in the menu but still need a name and an icon. */
const OTHER: NavItem[] = [
  { to: '/tickets/templates', label: 'Reply templates', icon: TextQuote },
  { to: '/profile', label: 'Your profile', icon: UserRound },
]

export function visibleNav(role: StaffRole | undefined) {
  return NAV.map((g) => ({
    ...g,
    items: g.items.filter((i) => !i.roles || (role && i.roles.includes(role))),
  })).filter((g) => g.items.length > 0)
}

/** The menu item a path belongs to: the longest match, so /customers/123 is Customers. */
export function navItemFor(pathname: string): NavItem | undefined {
  const all = [...OTHER, ...NAV.flatMap((g) => g.items)].filter((i) => !i.external)
  return all
    .filter((i) => (i.to === '/' ? pathname === '/' : pathname === i.to || pathname.startsWith(i.to + '/')))
    .sort((a, b) => b.to.length - a.to.length)[0]
}
